import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import {
  BarChart3,
  LogOut,
  Package,
  Phone,
  ShoppingBag,
  Lock,
  Truck,
  Calendar,
  X,
  MapPin,
  Mail,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Wallet,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  adminLogin,
  fetchAdminOrders,
  fetchAdminStats,
  updateAdminOrder,
  formatCAD,
  getAdminToken,
  setAdminToken,
} from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { LOGO_URL, CONTACT } from "../../mock";

function cents(n) {
  return formatCAD((Number(n) || 0) / 100);
}

function statusClass(status) {
  if (status === "paid") return "bg-emerald-100 text-emerald-800";
  if (status === "shipped") return "bg-sky-100 text-sky-800";
  if (status === "delivered") return "bg-slate-200 text-slate-800";
  if (status === "pending") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function statusLabel(status) {
  const map = {
    pending: "En attente paiement",
    paid: "Payée — à livrer",
    shipped: "Expédiée",
    delivered: "Livrée / reçue",
    cancelled: "Annulée",
    refunded: "Remboursée",
    failed: "Échouée",
  };
  return map[status] || status;
}

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("fr-CA", { month: "long", year: "numeric" });
}

function AdminLogin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Connexion refusée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white border border-border rounded-2xl p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Administration</p>
            <h1 className="text-xl font-bold text-slate-900">{CONTACT.brand}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
          <Lock className="h-4 w-4" /> Accès réservé — URL non listée dans le site public
        </div>
        <Label htmlFor="admin-pass">Mot de passe</Label>
        <Input
          id="admin-pass"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 mb-4"
          autoFocus
        />
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-full bg-brand-blue hover:bg-blue-800"
        >
          {loading ? "Connexion…" : "Se connecter"}
        </Button>
      </form>
    </div>
  );
}

function AdminShell({ children }) {
  const navigate = useNavigate();
  const logout = () => {
    setAdminToken("");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain bg-white rounded-full p-0.5" />
            <div className="min-w-0">
              <p className="text-xs text-slate-300">Panneau admin</p>
              <p className="font-semibold truncate">{CONTACT.brand}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-xs text-slate-300 hover:text-white hidden sm:inline">
              Voir le site
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={logout}
              className="h-9 rounded-full border-slate-600 bg-transparent text-white hover:bg-slate-800 gap-2"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-8">{children}</div>
    </div>
  );
}

function RequireAdmin({ children }) {
  if (!getAdminToken()) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        <div className="h-10 w-10 rounded-full bg-brand-blue/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-brand-blue" />
        </div>
      </div>
    </div>
  );
}

function OrderDetailModal({ order, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!order) return null;

  const mark = async (status) => {
    setBusy(true);
    setErr("");
    try {
      const data = await updateAdminOrder({ id: order.id, status });
      onUpdated(data.order);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const mapsQ = [order.address_line1, order.city, order.province, order.postal_code]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/50" aria-label="Fermer" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <p className="text-xs text-muted-foreground">Détail commande</p>
            <h3 className="text-lg font-bold text-slate-900">{order.order_number}</h3>
            <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(order.status)}`}>
              {statusLabel(order.status)}
            </span>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <section>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Truck className="h-4 w-4" /> Client & livraison
            </h4>
            <div className="rounded-xl border border-border bg-slate-50 p-4 text-sm space-y-2">
              <p className="font-medium text-slate-900 text-base">{order.customer_name || "—"}</p>
              <p className="flex items-center gap-2 text-slate-700">
                <Mail className="h-3.5 w-3.5" /> {order.email}
              </p>
              {order.phone && (
                <p className="flex items-center gap-2 text-slate-700">
                  <Phone className="h-3.5 w-3.5" />
                  <a href={`tel:${order.phone}`} className="hover:text-brand-blue">{order.phone}</a>
                </p>
              )}
              <p className="flex items-start gap-2 text-slate-700 pt-1">
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  {order.address_line1 || "Adresse non fournie"}
                  {order.city && (
                    <>
                      <br />
                      {order.city}
                      {order.province ? `, ${order.province}` : ""} {order.postal_code || ""}
                    </>
                  )}
                </span>
              </p>
              {mapsQ && (
                <a
                  href={`https://www.google.com/maps?q=${encodeURIComponent(mapsQ)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-brand-blue font-medium hover:underline pt-1"
                >
                  Ouvrir l&apos;itinéraire Maps
                </a>
              )}
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" /> Articles commandés
            </h4>
            <ul className="space-y-3">
              {(order.items || []).map((it, idx) => (
                <li
                  key={`${it.sku}-${idx}`}
                  className="flex gap-3 border border-border rounded-xl p-3"
                >
                  <div className="h-16 w-16 rounded-lg bg-slate-100 border border-border overflow-hidden shrink-0">
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-400">
                        <Package className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 line-clamp-2">{it.product_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">SKU {it.sku || "—"}</p>
                    <p className="text-sm mt-1">
                      {it.quantity}× {cents(it.unit_price_cents)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-border p-4 text-sm space-y-1">
            <div className="flex justify-between"><span>Sous-total</span><span>{cents(order.subtotal_cents)}</span></div>
            <div className="flex justify-between"><span>Livraison</span><span>{cents(order.shipping_cents)}</span></div>
            <div className="flex justify-between"><span>Taxes</span><span>{cents(order.tax_cents)}</span></div>
            <div className="flex justify-between font-bold text-base pt-2 border-t border-border">
              <span>Total</span><span>{cents(order.total_cents)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              Créée : {order.created_at ? new Date(order.created_at).toLocaleString("fr-CA") : "—"}
              {order.paid_at && <> · Payée : {new Date(order.paid_at).toLocaleString("fr-CA")}</>}
              {order.delivered_at && <> · Livrée : {new Date(order.delivered_at).toLocaleString("fr-CA")}</>}
            </p>
          </section>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="flex flex-col sm:flex-row gap-2 pb-2">
            {(order.status === "paid" || order.status === "pending") && (
              <Button
                type="button"
                disabled={busy}
                onClick={() => mark("shipped")}
                className="flex-1 rounded-full bg-sky-700 hover:bg-sky-800 gap-2"
              >
                <Truck className="h-4 w-4" /> Marquer expédiée
              </Button>
            )}
            {(order.status === "paid" || order.status === "shipped") && (
              <Button
                type="button"
                disabled={busy}
                onClick={() => mark("delivered")}
                className="flex-1 rounded-full bg-emerald-700 hover:bg-emerald-800 gap-2"
              >
                <CheckCircle2 className="h-4 w-4" /> Client a reçu — historique
              </Button>
            )}
            {order.status === "delivered" && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Cette commande est dans l&apos;historique (plus dans « à livrer »).
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrdersTable({ orders, onSelect, emptyText }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-3 font-medium">N°</th>
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Client</th>
            <th className="py-2 pr-3 font-medium">Statut</th>
            <th className="py-2 pr-3 font-medium text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {(orders || []).map((o) => (
            <tr
              key={o.id}
              className="border-b border-border/70 align-top cursor-pointer hover:bg-slate-50"
              onClick={() => onSelect(o)}
            >
              <td className="py-3 pr-3 font-medium text-slate-900 whitespace-nowrap">
                {o.order_number}
                <div className="text-xs text-muted-foreground font-normal mt-1">
                  {(o.items || []).slice(0, 2).map((it) => (
                    <div key={`${it.sku}-${it.product_name}`}>
                      {it.quantity}× {it.product_name}
                    </div>
                  ))}
                </div>
              </td>
              <td className="py-3 pr-3 whitespace-nowrap text-slate-600">
                {o.paid_at || o.created_at
                  ? new Date(o.paid_at || o.created_at).toLocaleString("fr-CA")
                  : "—"}
              </td>
              <td className="py-3 pr-3">
                <div className="text-slate-800">{o.customer_name || "—"}</div>
                <div className="text-xs text-muted-foreground">{o.email}</div>
                {o.city && (
                  <div className="text-xs text-muted-foreground">{o.city}</div>
                )}
              </td>
              <td className="py-3 pr-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(o.status)}`}>
                  {statusLabel(o.status)}
                </span>
              </td>
              <td className="py-3 text-right font-semibold whitespace-nowrap">{cents(o.total_cents)}</td>
            </tr>
          ))}
          {(orders || []).length === 0 && (
            <tr>
              <td colSpan={5} className="py-8 text-center text-muted-foreground">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activeOrders, setActiveOrders] = useState(null);
  const [historyOrders, setHistoryOrders] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [tab, setTab] = useState("active");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    const [s, active, hist] = await Promise.all([
      fetchAdminStats({ year: selectedYear }),
      fetchAdminOrders({ bucket: "active", page: 1, limit: 100 }),
      fetchAdminOrders({ bucket: "history", month: selectedMonth, page: 1, limit: 100 }),
    ]);
    setStats(s);
    setActiveOrders(active);
    setHistoryOrders(hist);
    setMonths(hist.months || active.months || []);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await reload();
      } catch (err) {
        if (err?.response?.status === 401) {
          setAdminToken("");
          navigate("/admin/login", { replace: true });
          return;
        }
        if (!cancelled) setError(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, reload]);

  const shiftMonth = (delta) => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const chartData = useMemo(() => {
    if (!stats) return [];
    return (stats.sales_by_month || []).map((row) => ({
      month: row.month,
      total: (row.revenue_cents || 0) / 100,
      en_cours: (row.in_progress_cents || 0) / 100,
      livre: (row.delivered_cents || 0) / 100,
      calls: 0,
    }));
  }, [stats]);

  const yearOptions = useMemo(() => {
    const years = new Set((stats?.sales_by_year || []).map((y) => y.year));
    years.add(new Date().getFullYear());
    years.add(selectedYear);
    return [...years].sort((a, b) => b - a);
  }, [stats, selectedYear]);

  const onOrderUpdated = (order) => {
    setSelected(order);
    reload().catch(() => {});
  };

  if (loading) {
    return (
      <AdminShell>
        <p className="text-muted-foreground">Chargement des statistiques…</p>
      </AdminShell>
    );
  }

  if (error) {
    return (
      <AdminShell>
        <p className="text-red-600">{error}</p>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">
            Suivi financier : le CA reste après confirmation de livraison
          </p>
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Année
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="h-9 rounded-lg border border-border px-3 bg-white font-medium text-slate-900"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        <StatCard
          icon={ShoppingBag}
          label="Ventes ce mois (CA)"
          value={cents(stats.month.revenue_cents)}
          hint={`${stats.month.orders} commande(s) payées — en cours + livrées`}
        />
        <StatCard
          icon={BarChart3}
          label={`Ventes ${stats.year_sales?.year || selectedYear}`}
          value={cents(stats.year_sales?.revenue_cents)}
          hint={`${stats.year_sales?.orders || 0} commande(s) sur l’année`}
        />
        <StatCard
          icon={Wallet}
          label="Ventes totales (tout temps)"
          value={cents(stats.all_time.revenue_cents)}
          hint={`${stats.all_time.orders} commande(s) cumulées`}
        />
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Truck}
          label="En cours (à livrer)"
          value={cents(stats.in_progress?.revenue_cents)}
          hint={`${stats.in_progress?.orders || 0} commande(s) payées / expédiées`}
        />
        <StatCard
          icon={CheckCircle2}
          label="Livré / reçu"
          value={cents(stats.delivered?.revenue_cents)}
          hint={`${stats.delivered?.orders || 0} commande(s) confirmées`}
        />
        <StatCard
          icon={Truck}
          label="Commandes à traiter"
          value={String(stats.in_progress?.orders ?? activeOrders?.total ?? 0)}
          hint="Liste « À livrer »"
        />
        <StatCard
          icon={Phone}
          label="Appels ce mois"
          value={String(stats.month.calls)}
          hint="Clics sur Appeler (contact)"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1">Revenus par mois (CAD)</h2>
          <p className="text-xs text-muted-foreground mb-4">
            En cours + livré = CA du mois (ne baisse pas après livraison)
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCAD(v)} />
                <Legend />
                <Bar dataKey="en_cours" stackId="a" name="En cours" fill="#0284c7" radius={[0, 0, 0, 0]} />
                <Bar dataKey="livre" stackId="a" name="Livré" fill="#1e3a5f" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <Package className="h-4 w-4" /> Produits les plus commandés
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Cumul de toutes les ventes payées (même après livraison)
          </p>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(stats.top_products || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune vente payée pour l’instant.</p>
            )}
            {(stats.top_products || []).map((p) => (
              <div key={`${p.sku}-${p.product_name}`} className="flex justify-between gap-3 text-sm border-b border-border/60 pb-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 line-clamp-1">{p.product_name}</p>
                  <p className="text-xs text-muted-foreground">{p.sku || "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold">{p.qty_sold}×</p>
                  <p className="text-xs text-muted-foreground">{cents(p.revenue_cents)}</p>
                </div>
              </div>
            ))}
          </div>
          {(stats.sales_by_year || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">CA par année</p>
              <div className="space-y-1.5">
                {stats.sales_by_year.map((y) => (
                  <div key={y.year} className="flex justify-between text-sm">
                    <span className="text-slate-700">{y.year}</span>
                    <span className="font-semibold">{cents(y.revenue_cents)} <span className="font-normal text-muted-foreground">({y.orders_count})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Gestion des commandes
          </h2>
          <div className="flex rounded-full border border-border p-1 bg-slate-50 w-fit">
            <button
              type="button"
              onClick={() => setTab("active")}
              className={`px-4 py-1.5 text-sm rounded-full ${
                tab === "active" ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-white"
              }`}
            >
              À livrer ({activeOrders?.total ?? 0})
            </button>
            <button
              type="button"
              onClick={() => setTab("history")}
              className={`px-4 py-1.5 text-sm rounded-full ${
                tab === "history" ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-white"
              }`}
            >
              Historique
            </button>
          </div>
        </div>

        {tab === "history" && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-slate-50"
              aria-label="Mois précédent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[180px] text-center">
              <p className="font-semibold text-slate-900 capitalize">{monthLabel(selectedMonth)}</p>
              <p className="text-xs text-muted-foreground">{historyOrders?.total ?? 0} commande(s)</p>
            </div>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-slate-50"
              aria-label="Mois suivant"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            {months.length > 0 && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="ml-auto h-9 rounded-lg border border-border px-3 text-sm bg-white"
              >
                {months.map((m) => (
                  <option key={m.month} value={m.month}>
                    {monthLabel(m.month)} ({m.orders_count})
                  </option>
                ))}
                {!months.find((m) => m.month === selectedMonth) && (
                  <option value={selectedMonth}>{monthLabel(selectedMonth)}</option>
                )}
              </select>
            )}
          </div>
        )}

        {tab === "active" ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              Cliquez une commande pour voir les articles (avec image), l&apos;adresse de livraison, puis marquez « Client a reçu » pour la déplacer dans l&apos;historique.
            </p>
            <OrdersTable
              orders={activeOrders?.orders}
              onSelect={setSelected}
              emptyText="Aucune commande en attente de livraison."
            />
          </>
        ) : (
          <OrdersTable
            orders={historyOrders?.orders}
            onSelect={setSelected}
            emptyText={`Aucune commande livrée en ${monthLabel(selectedMonth)}.`}
          />
        )}
      </div>

      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onUpdated={onOrderUpdated}
        />
      )}
    </AdminShell>
  );
}

export default function AdminApp() {
  return (
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route
        path="*"
        element={
          <RequireAdmin>
            <AdminDashboard />
          </RequireAdmin>
        }
      />
    </Routes>
  );
}
